import { genericMemo } from "@redotech/react-util/component";
import { useScrolled } from "@redotech/react-util/scroll";
import classNames from "classnames";
import * as React from "react";
import {
  CSSProperties,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Flex } from "../../flex";
import { RowClickHandler } from "../../table";
import { Text } from "../../text";
import { SpacingValue } from "../../theme/box";
import { RedoSpinner } from "../loading/redo-spinner";
import { CommandMenuProvider, RedoTableCellProps } from "./redo-table-cells";
import * as redoTableCellsCss from "./redo-table-cells.module.css";
import {
  MINIMUM_COLUMN_WIDTH,
  RedoTableColumnResizer,
  getResizedColumnWidth,
} from "./redo-table-column-resizer";
import {
  RedoTableExpandedRow,
  RedoTableRow,
  RedoTableSummaryRow,
} from "./redo-table-row";
import * as redoTableRowCss from "./redo-table-row.module.css";
import * as redoTableCss from "./redo-table.module.css";

export const redoTableSize = ["sm", "md"] as const;
export type RedoTableSize = (typeof redoTableSize)[number];

export const redoTableColumnWidthType = ["fixed", "hug", "fill"] as const;
export type RedoTableColumnWidthType =
  (typeof redoTableColumnWidthType)[number];

export type RedoTableColumnWidthMode =
  | { type: "fixed"; width: CSSProperties["width"] }
  | { type: "hug" }
  | {
      type: "fill";
      /** Will map 1 -> "1fr" https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_grid_layout/Basic_concepts_of_grid_layout#the_fr_unit*/
      relativeLength?: number;
      minLength?: CSSProperties["width"];
    };

export const redoTableColumnAlignment = ["left", "right", "center"] as const;
export type RedoTableColumnAlignment =
  (typeof redoTableColumnAlignment)[number];

export interface RedoTableColumn<DATA, SUMMARY = unknown> {
  key?: string;
  widthMode?: RedoTableColumnWidthMode;
  renderHeaderCell: () => React.ReactNode;
  renderNormalCell: (props: RedoTableCellProps<DATA>) => React.ReactNode;
  renderExpandedCell?: ({
    row,
    expansionIndex,
  }: {
    row: DATA;
    expansionIndex: number;
  }) => React.ReactNode;
  renderSummaryCell?: (props: { summary: SUMMARY }) => React.ReactNode;
  alignment?: RedoTableColumnAlignment;
  sticky?: boolean;
  disableColumnResize?: boolean;
}

export interface OnVisibleHandlers {
  setTopVisible: (topIsVisible: boolean) => void;
  setBottomVisible: (bottomIsVisible: boolean) => void;
}

export const redoTableHeaderColor = ["white", "grey"] as const;
export type RedoTableHeaderColor = (typeof redoTableHeaderColor)[number];

export interface RedoTableLayout<DATA, SUMMARY = unknown> {
  columns: RedoTableColumn<DATA, SUMMARY>[];
  headerColor?: RedoTableHeaderColor;
  standalone?: boolean;
}

export interface RedoTableSummaryConfig<SUMMARY> {
  data: SUMMARY;
  position: "top" | "bottom";
}

export interface RedoTableProps<DATA, SUMMARY = unknown> {
  size?: RedoTableSize;
  layout: RedoTableLayout<DATA, SUMMARY>;
  rowsOfData: DATA[];
  rowClassName?: (row: DATA, rowIndex: number) => string | undefined;
  isRowClickable?: (row: DATA, rowIndex: number) => boolean;
  onRowClick?: (
    row: DATA,
    rowIndex: number,
    sourceEvent: React.MouseEvent,
  ) => void;
  onContextMenu?: RowClickHandler<DATA>;
  onRowHovered?(record?: DATA): void;
  rowsSelected?: boolean[];
  onVisibleHandlers?: OnVisibleHandlers;
  emptyStateContent?: React.ReactNode;
  loadingStateContent?: React.ReactNode;
  additionalClasses?: string;
  "data-arbiter"?: string;
  hideHeader?: boolean;
  hideHeaderWhenEmpty?: boolean;
  rowsExpansions?: Record<number, { expanded: boolean; numExpansions: number }>;
  /**
   * Render a row's expansion as one full-width block spanning all columns
   * (auto-sized to its content) instead of per-column cells.
   */
  renderExpandedRow?: (row: DATA, rowIndex: number) => React.ReactNode;
  aggregationRow?: React.ReactNode;
  maxAvailableHeight?: number;
  summary?: RedoTableSummaryConfig<SUMMARY>;
  autoRowHeight?: boolean;
  fillStandaloneBody?: boolean;
  adjustableColumnWidths?: boolean;
  columnWidthsStorageKey?: string;
}

function getColumnKey<DATA, SUMMARY = unknown>(
  column: RedoTableColumn<DATA, SUMMARY>,
  columnIndex: number,
): string {
  return column.key ?? `column-${columnIndex}`;
}

function getNumericWidth(
  width: CSSProperties["width"] | undefined,
): number | undefined {
  if (typeof width === "number") {
    return width;
  }
  if (typeof width === "string") {
    const parsedWidth = parseInt(width, 10);
    return Number.isNaN(parsedWidth) ? undefined : parsedWidth;
  }
  return undefined;
}

function getColumnInitialWidth<DATA, SUMMARY = unknown>(
  column: RedoTableColumn<DATA, SUMMARY> | undefined,
): number | undefined {
  if (!column) {
    return undefined;
  }
  if (column.widthMode?.type === "fixed") {
    return getNumericWidth(column.widthMode.width);
  }
  if (column.widthMode?.type === "fill") {
    return getNumericWidth(column.widthMode.minLength);
  }
  return undefined;
}

function isColumnWidths(value: unknown): value is Record<string, number> {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    Object.values(value).every(
      (width) =>
        typeof width === "number" &&
        Number.isFinite(width) &&
        width >= MINIMUM_COLUMN_WIDTH,
    )
  );
}

function getStoredColumnWidths(
  columnWidthsStorageKey: string | undefined,
): Record<string, number> {
  if (
    !columnWidthsStorageKey ||
    typeof globalThis.localStorage === "undefined"
  ) {
    return {};
  }

  try {
    const stored = globalThis.localStorage.getItem(columnWidthsStorageKey);
    if (!stored) {
      return {};
    }
    const parsed: unknown = JSON.parse(stored);
    return isColumnWidths(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function getColumnWidthMode<DATA, SUMMARY>(
  column: RedoTableColumn<DATA, SUMMARY>,
  width: number,
): RedoTableColumnWidthMode {
  if (column.widthMode?.type === "fill") {
    return { ...column.widthMode, minLength: width };
  }
  return { type: "fixed", width };
}

export const tableDimensionToGridTemplateStyles = (
  dim: RedoTableColumnWidthMode,
) => {
  return dim.type === "fixed"
    ? typeof dim.width === "number"
      ? `minmax(${dim.width}px, 0fr)`
      : `minmax(${dim.width}, 0fr)`
    : dim.type === "hug"
      ? "min-content"
      : typeof dim.minLength === "number"
        ? `minmax(${dim.minLength || 0}px, ${dim.relativeLength || 1}fr)`
        : `minmax(${dim.minLength || 0}, ${dim.relativeLength || 1}fr)`;
};

export const RedoTableHeaderRow = genericMemo(function RedoTableHeaderRow<
  DATA,
>({
  adjustableColumnWidths = false,
  headerColor = "grey",
  columns,
  heldColumnKey,
  onAdjustColumnWidth,
  onResizeStart,
  setHeldColumnKey,
}: {
  adjustableColumnWidths?: boolean;
  headerColor?: RedoTableHeaderColor;
  columns: RedoTableColumn<DATA, unknown>[];
  heldColumnKey?: string | null;
  onAdjustColumnWidth?: (columnKey: string, widthDif: number) => void;
  onResizeStart?: (columnKey: string, width: number) => void;
  setHeldColumnKey?: (columnKey: string | null) => void;
}) {
  return (
    <>
      {columns.map((column, columnIndex) => {
        const columnKey = getColumnKey(column, columnIndex);
        const canResize = Boolean(
          adjustableColumnWidths &&
          !column.disableColumnResize &&
          onAdjustColumnWidth &&
          setHeldColumnKey,
        );

        return (
          <div
            className={classNames(
              redoTableCellsCss.cell,
              redoTableCss.headerCell,
              headerColorStyle[headerColor],
            )}
            data-arbiter="RedoTableHeaderRow"
            key={`header-${columnKey}`}
            role="columnheader"
          >
            <column.renderHeaderCell />
            {canResize && (
              <RedoTableColumnResizer
                held={heldColumnKey === columnKey}
                onAdjustColumnWidth={({ widthDif }) =>
                  onAdjustColumnWidth?.(columnKey, widthDif)
                }
                onResizeStart={
                  onResizeStart
                    ? ({ width }) => onResizeStart(columnKey, width)
                    : undefined
                }
                setHeld={(held) => setHeldColumnKey?.(held ? columnKey : null)}
                showSeparator
              />
            )}
          </div>
        );
      })}
    </>
  );
});

/**
 * Arbiter table https://www.figma.com/design/iZHj2I36zd9i8nRbWKw4ZK/%E2%9D%96-Arbiter?node-id=214-0&node-type=canvas&t=TZXhtoU5Ez2uwaXW-0
 *
 * Req. reading: https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_grid_layout/Basic_concepts_of_grid_layout
 *
 * To be as presentational as possible, the table knows as little as possible.
 * Wrap with your own logical layer to manage backing row DATA, request/fetch new data upon table interactions, implement sorts, etc...
 * For extra features e.g. paginated footer, use kits that wrap this.
 */
export const RedoTable = genericMemo(function RedoTable<
  DATA,
  SUMMARY = unknown,
>({
  adjustableColumnWidths = false,
  columnWidthsStorageKey,
  size = "sm",
  layout: { columns, headerColor = "grey", standalone = false },
  rowsOfData,
  onRowClick,
  onContextMenu,
  onRowHovered,
  rowClassName,
  isRowClickable,
  rowsSelected,
  onVisibleHandlers,
  emptyStateContent = null,
  loadingStateContent = null,
  additionalClasses,
  "data-arbiter": dataArbiter = "RedoTable",
  hideHeader,
  hideHeaderWhenEmpty,
  rowsExpansions,
  renderExpandedRow,
  aggregationRow,
  summary,
  autoRowHeight = false,
  fillStandaloneBody = false,
}: RedoTableProps<DATA, SUMMARY>) {
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() =>
    getStoredColumnWidths(columnWidthsStorageKey),
  );
  const resizeStartWidthsRef = useRef<Record<string, number>>({});
  const [heldColumnKey, setHeldColumnKey] = useState<string | null>(null);

  useEffect(() => {
    setColumnWidths(getStoredColumnWidths(columnWidthsStorageKey));
    resizeStartWidthsRef.current = {};
  }, [columnWidthsStorageKey]);

  useEffect(() => {
    if (
      !columnWidthsStorageKey ||
      typeof globalThis.localStorage === "undefined"
    ) {
      return;
    }
    try {
      globalThis.localStorage.setItem(
        columnWidthsStorageKey,
        JSON.stringify(columnWidths),
      );
    } catch {
      // Ignore storage failures; resizing still works for the current render.
    }
  }, [columnWidths, columnWidthsStorageKey]);

  const columnsWithWidths = useMemo(
    () =>
      columns.map((column, columnIndex) => {
        const width = columnWidths[getColumnKey(column, columnIndex)];
        return width === undefined
          ? column
          : { ...column, widthMode: getColumnWidthMode(column, width) };
      }),
    [columnWidths, columns],
  );

  const handleResizeStart = (columnKey: string, width: number) => {
    const column = columns.find(
      (candidate, columnIndex) =>
        getColumnKey(candidate, columnIndex) === columnKey,
    );
    resizeStartWidthsRef.current[columnKey] =
      width || getColumnInitialWidth(column) || MINIMUM_COLUMN_WIDTH;
  };

  const handleAdjustColumnWidth = (columnKey: string, widthDif: number) => {
    setColumnWidths((previousWidths) => {
      const column = columns.find(
        (candidate, columnIndex) =>
          getColumnKey(candidate, columnIndex) === columnKey,
      );
      const currentWidth =
        resizeStartWidthsRef.current[columnKey] ??
        getColumnInitialWidth(column) ??
        MINIMUM_COLUMN_WIDTH;
      const nextWidth = getResizedColumnWidth({ currentWidth, widthDif });
      if (previousWidths[columnKey] === nextWidth) {
        return previousWidths;
      }
      return { ...previousWidths, [columnKey]: nextWidth };
    });
  };

  /** req. reading: https://developer.mozilla.org/en-US/docs/Web/CSS/grid-template-columns */
  const styleForGridColTemplate = columnsWithWidths
    .map((column) =>
      tableDimensionToGridTemplateStyles(column.widthMode || { type: "fill" }),
    )
    .join(" ");

  const styleForGridRow = sizeToTemplateRowHeight[size];
  const styleForHeaderRow = sizeToTemplateHeaderRowHeight[size];

  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const hasLoadingStateContent = Boolean(loadingStateContent);
  const shouldShowHeader =
    !hideHeader && !(hideHeaderWhenEmpty && rowsOfData.length === 0);
  const showFullHeightState =
    rowsOfData.length === 0 &&
    (hasLoadingStateContent || Boolean(emptyStateContent));
  const fullHeightStateContent = hasLoadingStateContent
    ? loadingStateContent
    : emptyStateContent;
  const showStandaloneBodyFill =
    standalone &&
    fillStandaloneBody &&
    !showFullHeightState &&
    rowsOfData.length > 0;
  const bodyRowCount = rowsOfData.reduce((total, _, index) => {
    const numExpansions = rowsExpansions?.[index]?.expanded
      ? (rowsExpansions[index].numExpansions ?? 0)
      : 0;
    return total + 1 + numExpansions;
  }, 0);
  const styleForBodyRow = autoRowHeight ? "auto" : styleForGridRow;
  const gridTemplateRows = showFullHeightState
    ? shouldShowHeader
      ? `${styleForHeaderRow} minmax(0, 1fr)`
      : "minmax(0, 1fr)"
    : showStandaloneBodyFill
      ? [
          shouldShowHeader ? styleForHeaderRow : undefined,
          ...Array(bodyRowCount).fill(styleForBodyRow),
          "minmax(0, 1fr)",
        ]
          .filter(Boolean)
          .join(" ")
      : hideHeader
        ? undefined
        : styleForHeaderRow;
  const scrolled = useScrolled(scrollAreaRef.current);
  useEffect(() => {
    if (!scrollAreaRef.current) {
      return;
    }
    onVisibleHandlers?.setTopVisible(scrolled.top);
    onVisibleHandlers?.setBottomVisible(scrolled.bottom);
  }, [scrolled, onVisibleHandlers]);

  return (
    <CommandMenuProvider>
      <div
        className={classNames(redoTableCss.table, additionalClasses, {
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          [redoTableCss.standaloneTable]: standalone,
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          [redoTableCss.standaloneTableWithBodyFill]: showStandaloneBodyFill,
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          [redoTableCss.fullHeightState]: showFullHeightState,
        })}
        data-arbiter={dataArbiter}
        data-standalone-body-fill={showStandaloneBodyFill ? "true" : undefined}
        ref={scrollAreaRef}
        role="table"
        style={{
          gridTemplateColumns: styleForGridColTemplate,
          gridTemplateRows,
          gridAutoRows:
            showFullHeightState || showStandaloneBodyFill
              ? undefined
              : autoRowHeight
                ? "auto"
                : styleForGridRow,
        }}
      >
        {shouldShowHeader && (
          <div
            className={classNames(redoTableCss.rowWrapper)}
            key="header"
            role="row"
          >
            <RedoTableHeaderRow
              adjustableColumnWidths={adjustableColumnWidths}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              columns={columnsWithWidths as RedoTableColumn<DATA, unknown>[]}
              headerColor={headerColor}
              heldColumnKey={heldColumnKey}
              onAdjustColumnWidth={handleAdjustColumnWidth}
              onResizeStart={handleResizeStart}
              setHeldColumnKey={setHeldColumnKey}
            />
          </div>
        )}
        {summary?.position === "top" && !hasLoadingStateContent && (
          <div
            className={redoTableCss.rowWrapper}
            key="summary"
            role="rowgroup"
          >
            <RedoTableSummaryRow
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              columns={columnsWithWidths as RedoTableColumn<unknown, SUMMARY>[]}
              summary={summary.data}
            />
          </div>
        )}
        {rowsOfData.length > 0 && (
          <div className={redoTableCss.rowWrapper} role="rowgroup">
            {rowsOfData.map((row, rowIndex) => {
              const expanded = rowsExpansions?.[rowIndex]?.expanded;
              const numExpansions =
                rowsExpansions?.[rowIndex]?.numExpansions ?? 0;
              return (
                <React.Fragment key={`row-group-${rowIndex}`}>
                  <RedoTableRow<DATA>
                    className={classNames(
                      redoTableRowCss.hoverable,
                      rowClassName?.(row, rowIndex),
                    )}
                    columns={
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                      columnsWithWidths as RedoTableColumn<DATA, unknown>[]
                    }
                    isRowClickable={isRowClickable}
                    onContextMenu={onContextMenu}
                    onRowClick={onRowClick}
                    onRowHovered={onRowHovered}
                    row={row}
                    rowIndex={rowIndex}
                    rowSelected={rowsSelected?.[rowIndex]}
                  />
                  {expanded && renderExpandedRow ? (
                    <div role="row" style={{ gridColumn: "1 / -1" }}>
                      {renderExpandedRow(row, rowIndex)}
                    </div>
                  ) : (
                    expanded &&
                    Array.from({ length: numExpansions }).map(
                      (_, expansionIndex) => {
                        const rowKey = `row-${rowIndex}-exp${expansionIndex}`;
                        return (
                          <RedoTableExpandedRow<DATA>
                            columns={
                              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                              columnsWithWidths as RedoTableColumn<
                                DATA,
                                unknown
                              >[]
                            }
                            expansionIndex={expansionIndex}
                            key={rowKey}
                            onRowClick={onRowClick}
                            row={row}
                            rowIndex={rowIndex}
                          />
                        );
                      },
                    )
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}
        {summary?.position === "bottom" && !hasLoadingStateContent && (
          <div
            className={redoTableCss.rowWrapper}
            key="summary"
            role="rowgroup"
          >
            <RedoTableSummaryRow
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              columns={columnsWithWidths as RedoTableColumn<unknown, SUMMARY>[]}
              summary={summary.data}
            />
          </div>
        )}
        {hasLoadingStateContent && rowsOfData.length > 0 && (
          <div className={redoTableCss.rowWrapper} role="row">
            <Flex
              align="center"
              className={redoTableCss.loadingStateCell}
              justify="center"
              style={{ gridColumn: "1 / -1" }}
            >
              {loadingStateContent}
            </Flex>
          </div>
        )}
        {showFullHeightState && (
          <div className={redoTableCss.rowWrapper} role="row">
            <div
              className={redoTableCss.stateCell}
              role="cell"
              style={{ gridColumn: "1 / -1" }}
            >
              {fullHeightStateContent}
            </div>
          </div>
        )}
        {showStandaloneBodyFill && (
          <div className={redoTableCss.rowWrapper} role="row">
            <div
              className={classNames(
                redoTableCss.tableBodyFiller,
                redoTableCellsCss.cell,
              )}
              data-table-body-filler="true"
              role="cell"
              style={{ gridColumn: "1 / -1" }}
            />
          </div>
        )}
        {aggregationRow && (
          <div style={{ gridColumn: "1 / -1" }}>{aggregationRow}</div>
        )}
      </div>
    </CommandMenuProvider>
  );
});

export const sizeToTemplateRowHeight: Record<
  RedoTableSize,
  CSSProperties["height"]
> = { ["sm"]: "48px", ["md"]: "72px" };

export const sizeToTemplateHeaderRowHeight: Record<
  RedoTableSize,
  CSSProperties["height"]
> = { ["sm"]: "36px", ["md"]: "44px" };

export const headerColorStyle: Record<
  RedoTableHeaderColor,
  string | undefined
> = { ["white"]: redoTableCss.white, ["grey"]: undefined };

export const RedoTableLoadingStateContent = memo(
  function RedoTableLoadingStateContent({
    isNextPage = false,
  }: { isNextPage?: boolean } = {}) {
    return (
      <Flex
        align="center"
        data-arbiter="RedoTableLoadingStateContent"
        grow={1}
        height="full"
        justify="center"
        pt={isNextPage ? "lg" : undefined}
        w="full"
      >
        <RedoSpinner size="md" style="track" />
      </Flex>
    );
  },
);

export const RedoTableEmptyStateContent = memo(
  function RedoTableEmptyStateContent({
    text,
    py = "3xl",
  }: {
    text: string;
    py?: SpacingValue;
  }) {
    return (
      <Flex
        align="center"
        data-arbiter="RedoTableEmptyStateContent"
        grow={1}
        justify="center"
        py={py}
      >
        <Text fontSize="sm" fontWeight="regular" textColor="primary">
          {text}
        </Text>
      </Flex>
    );
  },
);
